/**
 * What a crown health screen's `crown_health.json` says (forestum `crown_health`): the crowns
 * per category, per genus and category, and the hotspots. Read leniently: a figure missing or
 * of the wrong type is left out, never guessed.
 */
import { HEALTH_CATEGORIES, type HealthCategory } from '@/map/layers';

export const CROWN_HEALTH_FILE = 'crown_health.json';
export const CROWN_HEALTH_GPKG = 'crown_health.gpkg';

export interface CategoryFigures {
  crowns: number;
  areaM2: number | null;
  volumeM3: number | null;
}

export interface GenusRow {
  /** As the screen names it: a Latin genus, or `snag`, `unknown`, `shrub`. */
  genus: string;
  role: string | null;
  /** What its crowns were compared with: `genus`, `living` or `none`. */
  reference: string | null;
  crowns: number;
  categories: Partial<Record<HealthCategory, number>>;
}

export interface Hotspot {
  hotspot: number;
  flagged: number;
  dead: number;
  snag: number;
  declining: number;
  threatened: number;
  mainGenus: string | null;
  genera: string | null;
  volumeM3: number | null;
  worstStress: number | null;
  areaM2: number | null;
}

/** The screen's thresholds the page explains its categories with; forestum's defaults where the file has none. */
export interface HealthParams {
  neighbourRadiusM: number;
  deadNdvi: number;
  declineZ: number;
  threatRadiusM: number;
  hotspotEpsM: number;
  hotspotMinCrowns: number;
}

export const DEFAULT_PARAMS: HealthParams = { neighbourRadiusM: 50, deadNdvi: 0.5, declineZ: -3, threatRadiusM: 20, hotspotEpsM: 20, hotspotMinCrowns: 3 };

export interface CrownHealthSummary {
  params: HealthParams;
  crowns: number | null;
  screened: number | null;
  categories: Partial<Record<HealthCategory, CategoryFigures>>;
  genera: GenusRow[];
  hotspots: { count: number; areaM2: number | null; flagged: number | null; top: Hotspot[] } | null;
}

const isObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

function hotspotOf(raw: unknown): Hotspot | null {
  if (!isObject(raw) || num(raw.hotspot) === null) return null;
  return {
    hotspot: num(raw.hotspot)!,
    flagged: num(raw.n_flagged) ?? 0,
    dead: num(raw.dead) ?? 0,
    snag: num(raw.snag) ?? 0,
    declining: num(raw.declining) ?? 0,
    threatened: num(raw.threatened) ?? 0,
    mainGenus: str(raw.main_genus),
    genera: str(raw.genera),
    volumeM3: num(raw.volume_m3),
    worstStress: num(raw.worst_stress_z),
    areaM2: num(raw.area_m2),
  };
}

/** The file as the page reads it, or null when it is not a crown health summary at all. */
export function parseCrownHealth(raw: unknown): CrownHealthSummary | null {
  if (!isObject(raw) || !isObject(raw.categories)) return null;
  const categories: CrownHealthSummary['categories'] = {};
  for (const c of HEALTH_CATEGORIES) {
    const v = raw.categories[c];
    if (!isObject(v) || num(v.crowns) === null) continue;
    categories[c] = { crowns: num(v.crowns)!, areaM2: num(v.crown_area_m2), volumeM3: num(v.volume_m3) };
  }
  const genera: GenusRow[] = [];
  for (const [genus, v] of Object.entries(isObject(raw.genera) ? raw.genera : {})) {
    if (!isObject(v)) continue;
    const per: GenusRow['categories'] = {};
    const cats = isObject(v.categories) ? v.categories : {};
    for (const c of HEALTH_CATEGORIES) {
      const n = isObject(cats[c]) ? num(cats[c].crowns) : num(cats[c]);
      if (n !== null) per[c] = n;
    }
    const total = num(v.crowns) ?? Object.values(per).reduce((a, b) => a + b, 0);
    genera.push({ genus, role: str(v.role), reference: str(v.reference), crowns: total, categories: per });
  }
  genera.sort((a, b) => b.crowns - a.crowns || a.genus.localeCompare(b.genus));
  const h = isObject(raw.hotspots) ? raw.hotspots : null;
  const hotspots = h
    ? {
        count: num(h.count) ?? 0,
        areaM2: num(h.area_m2),
        flagged: num(h.flagged_crowns),
        top: (Array.isArray(h.top) ? h.top : []).map(hotspotOf).filter((x): x is Hotspot => x !== null),
      }
    : null;
  const p = isObject(raw.params) ? raw.params : {};
  const params: HealthParams = {
    neighbourRadiusM: num(p.neighbour_radius_m) ?? DEFAULT_PARAMS.neighbourRadiusM,
    deadNdvi: num(p.dead_ndvi) ?? DEFAULT_PARAMS.deadNdvi,
    declineZ: num(p.decline_z) ?? DEFAULT_PARAMS.declineZ,
    threatRadiusM: num(p.threat_radius_m) ?? DEFAULT_PARAMS.threatRadiusM,
    hotspotEpsM: num(p.hotspot_eps_m) ?? DEFAULT_PARAMS.hotspotEpsM,
    hotspotMinCrowns: num(p.hotspot_min_crowns) ?? DEFAULT_PARAMS.hotspotMinCrowns,
  };
  return { params, crowns: num(raw.crowns), screened: num(raw.screened), categories, genera, hotspots };
}

/** The crowns per category, for the map's legend. */
export function categoryCounts(summary: CrownHealthSummary): Partial<Record<HealthCategory, number>> {
  return Object.fromEntries(Object.entries(summary.categories).map(([c, f]) => [c, f.crowns]));
}
